import { useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon } from 'lucide-react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ZendeskIntegration } from '@/shared/types/integration';
import { getZendeskAuthUrl } from '@/util/constants';
import { errorMessage } from '@/util/validation/helpers';
import { zendeskOAuthSchema } from '@/util/validation/rules/integration-rules';

type Form = InferType<typeof zendeskOAuthSchema>;

interface AuthorizationProps {
  callbackUrl?: string;
  isUpdated: boolean;
  isUpdating: boolean;
  integration: ZendeskIntegration;
  updateIntegration: (integration: ZendeskIntegration) => void;
}

const createOAuthURL = (integration: ZendeskIntegration) => {
  const queryParams = new URLSearchParams({
    redirect_uri: integration?.config?.redirect_uri ?? '',
    subdomain: integration?.config?.subdomain ?? '',
    client_id: integration?.config?.client_id ?? '',
    response_type: 'code',
    scope: 'read write chat',
  });

  return `${getZendeskAuthUrl(integration?.config?.subdomain)}?${queryParams}`;
};

const AuthorizationFieldset = ({
  callbackUrl = '',
  isUpdated,
  isUpdating,
  integration,
  updateIntegration,
}: AuthorizationProps) => {
  const { t } = useTranslation();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
  } = useForm<Form>({
    resolver: yupResolver(zendeskOAuthSchema) as Resolver<Form>,
    mode: 'onChange',
    defaultValues: integration?.config,
  });

  const zendeskUrl = createOAuthURL(integration);

  useEffect(() => {
    if (shouldRedirect && isUpdated && !isUpdating) {
      window.open(zendeskUrl, '_self');
      setShouldRedirect(false);
    }
  }, [isUpdated, isUpdating, shouldRedirect, zendeskUrl]);

  const onSubmit = useCallback<(form: Form) => void>(() => {
    const { client_secret, client_id, subdomain } = getValues();

    updateIntegration({
      ...integration,
      config: { client_secret, client_id, subdomain, redirect_uri: callbackUrl },
    });

    setShouldRedirect(true);
  }, [callbackUrl, getValues, integration, updateIntegration]);

  return (
    <div>
      <FormFieldset
        title={t('integrations.zendesk.auth.title')}
        subtitle={t('integrations.zendesk.auth.subtitle')}
        primaryButton={{
          children: t('integrations.zendesk.auth.connect'),
          isLoading: isUpdating,
          disabled: !isValid,
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          <div className="mb-6">
            <Label htmlFor="client_id" className={cn('mb-2', !!errors.client_id && 'text-warning')}>
              {t('integrations.zendesk.auth.client_id')}
            </Label>
            <Controller
              control={control}
              name="client_id"
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  id="client_id"
                  size="sm"
                  autoComplete="off"
                  aria-invalid={!!errors.client_id}
                  {...field}
                  value={field.value ?? ''}
                />
              )}
            />
            {errorMessage({ field: errors.client_id }) && (
              <p className="mt-1 text-sm text-warning">
                {errorMessage({ field: errors.client_id })}
              </p>
            )}
          </div>
          <div className="mb-6">
            <Label
              htmlFor="client_secret"
              className={cn('mb-2', !!errors.client_secret && 'text-warning')}
            >
              {t('integrations.zendesk.auth.client_secret')}
            </Label>
            <Controller
              control={control}
              name="client_secret"
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  id="client_secret"
                  size="sm"
                  autoComplete="off"
                  aria-invalid={!!errors.client_secret}
                  {...field}
                  value={field.value ?? ''}
                />
              )}
            />
            {errorMessage({ field: errors.client_secret }) && (
              <p className="mt-1 text-sm text-warning">
                {errorMessage({ field: errors.client_secret })}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="subdomain" className={cn('mb-2', !!errors.subdomain && 'text-warning')}>
              {t('integrations.zendesk.auth.subdomain')}
              <InfoTooltip content={t('integrations.zendesk.auth.identifier')} side="right">
                <CircleHelpIcon size={12} className="text-foreground-muted" />
              </InfoTooltip>
            </Label>
            <Controller
              control={control}
              name="subdomain"
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  id="subdomain"
                  size="sm"
                  autoComplete="off"
                  placeholder="mycompany"
                  aria-invalid={!!errors.subdomain}
                  {...field}
                  value={field.value ?? ''}
                />
              )}
            />
            {errorMessage({ field: errors.subdomain }) && (
              <p className="mt-1 text-sm text-warning">
                {errorMessage({ field: errors.subdomain })}
              </p>
            )}
          </div>
        </div>
      </FormFieldset>
    </div>
  );
};
export default AuthorizationFieldset;
